namespace advisorop.components.advisor {

    // "Add case" form. The template lays the fields out by hand, but what the advisor is
    // offered comes from Salesforce: the case types, the labels, which fields belong to the
    // chosen type, which of them are mandatory, and every picklist option. Nothing is read
    // from AdvisorOp.
    //
    // Because field membership is answered by Salesforce, the template can list every field
    // across the case types it supports and let has() decide what to show - an open-investment
    // case has Advisory Fee and beneficiaries, an alternative-investment case has Custodian
    // and Reinvest Dividends instead.
    export class PopSalesforceCaseNewController {

        // Which controller to talk to - the advisor one narrows to the signed-in advisor, the
        // servicer one never does. Set from the modal's resolve; advisor when nothing is passed.
        apiScope: services.SalesforceCaseScope;
        // Prefills the Household lookup when the case is opened from a household dashboard.
        household: domains.ISalesforceLookupResult;

        recordTypes: domains.ISalesforceCaseRecordType[] = [];
        selectedRecordType: domains.ISalesforceCaseRecordType = null;

        // 1 = choose the case type, 2 = fill the form. Salesforce asks the same question
        // first, and the answer decides which fields the form even has.
        step: number = 1;

        // Payload, keyed by Salesforce API name.
        model: domains.ISalesforceCaseCreate = { RecordTypeId: null };

        // Field metadata from Salesforce, indexed by API name.
        private fields: { [name: string]: domains.ISalesforceCaseField } = {};

        // Display text for the lookup type-aheads, kept apart from the ids in model.
        selectedContact: any = null;
        selectedSecondaryContact: any = null;
        selectedHousehold: any = null;
        selectedAdvisorUser: any = null;
        selectedAnalyst: any = null;
        selectedOwner: any = null;

        loading = true;
        saving = false;
        // Salesforce API names of fields it rejected, so they can be flagged.
        invalidFields: string[] = [];
        errorMessage: string = null;

        loadOptions: DevExpress.ui.dxLoadPanelOptions = {
            shadingColor: "rgba(0,0,0,0.4)",
            position: { of: "#popSalesforceCaseNew" },
            showIndicator: true,
            showPane: true,
            shading: false,
            closeOnOutsideClick: false,
            message: "Loading case setup from Salesforce...",
            bindingOptions: {
                visible: "vm.loading"
            }
        };

        dateNeededOptions: any = {
            bindingOptions: {
                value: "vm.model.Date_Paperwork_Needed__c"
            },
            type: "date",
            displayFormat: "MM/dd/yyyy",
            showClearButton: true,
            useMaskBehavior: true
        };

        static $inject = ["salesforceCaseApi", "generalService", "$scope", "$timeout"];
        constructor(
            private api: advisorop.services.SalesforceCaseApi,
            private gs: advisorop.services.GeneralService,
            private $scope: ng.IScope,
            private $timeout: ng.ITimeoutService
        ) {
        }

        // What each case type puts in front of the advisor, taken from Salesforce's own New
        // Case screens. Keyed by DeveloperName, because record type ids differ between orgs.
        //
        // Per type rather than one shared list because the forms genuinely diverge: an annuity
        // asks for Funding Amount where insurance asks for Benefit Amount, and insurance alone
        // adds Product Type and Purpose. The layout the integration user is on cannot tell the
        // two apart - it returns one merged field set for both record types.
        //
        // Everything left out still behaves: Status and Priority post the defaults Salesforce
        // reports, Advisor is filled from the signed-in user, and Household follows the chosen
        // contact. What is dropped is the data entry, not the data.
        //
        // TEMPORARY. Salesforce decides field membership per profile and the integration user
        // is on a fuller layout than advisors are, so these lists stand in for the layout we
        // cannot see. Once aosupport is pointed at the advisor layout they can go.
        private static commonFields: string[] = [
            "Anaylst__c",
            "OwnerId",
            "ContactId",
            "Client_Name_Secondary__c",
            "Beneficiaries_Y_N__c",
            // Shown only when the answer is Yes - see wantsBeneficiaries().
            "Beneficiaries__c",
            "Contingent_Beneficiary_ies__c",
            "Account_Name__c",
            "Account_Type__c",
            // Account_Subtype__c, not the near-identical Acct_Sub_Type__c that the integration
            // user's layout carries - this is the one advisors see and the case list reads.
            "Account_Subtype__c",
            "Funding_Method1__c",
            "Signing_Method__c",
            "Signer_Contact_Information__c",
            "Signing_Priority__c",
            "Alternate_Contact_Information__c",
            "Advisor_Analyst_Comments_and_Notes__c"
        ];

        // 1a / 1b / 1c - open investment at Pershing, Schwab, Fidelity.
        private static investmentFields: string[] = [
            "Account_Number__c",
            "Investment_Type__c",
            "Investment_Names_Amounts__c",
            "Funding_Amount__c",
            "Advisory_Fee__c"
        ];

        // 4 - trade into an alternative investment, against an account that already exists.
        private static alternativeFields: string[] = [
            "Custodian__c",
            "Existing_Acct_Number__c",
            "Investment_Type__c",
            "Investment_Names_Amounts__c",
            "Reinvest_Dividends__c",
            "Funding_Amount__c"
        ];

        // 5 - open annuity.
        private static annuityFields: string[] = [
            "Carrier__c",
            "Product_Name__c",
            "Illustration_Needed__c",
            "Funding_Amount__c",
            "Additional_Funding_Details__c",
            "Commission_Type__c",
            "Driver_s_License_On_File__c"
        ];

        // 6 - open insurance. Benefit Amount replaces Funding Amount here.
        private static insuranceFields: string[] = [
            "Carrier__c",
            "Product_Name__c",
            "Illustration_Needed__c",
            "Product_Type__c",
            "Purpose__c",
            "Benefit_Amount__c",
            "Additional_Funding_Details__c",
            "Commission_Type__c",
            "Driver_s_License_On_File__c"
        ];

        // Filled in by AdvisorOp or defaulted by Salesforce, so they survive a change of case
        // type even though the advisor is never asked for them.
        private static managedFields: string[] = [
            "Advisor_User__c",
            "FinServ__Household__c",
            "Status",
            "Priority",
            "Origin"
        ];

        private formFieldsFor(developerName: string): string[] {
            const c = PopSalesforceCaseNewController;

            switch (developerName) {
                case "Alternative_Investment": return c.commonFields.concat(c.alternativeFields);
                case "Annuity_App": return c.commonFields.concat(c.annuityFields);
                case "New_Insurance_App_Request": return c.commonFields.concat(c.insuranceFields);
                default: return c.commonFields.concat(c.investmentFields);
            }
        }

        // Which fields the chosen case type shows, rebuilt on every load.
        private shown: { [name: string]: boolean } = {};

        // --- Salesforce-driven field questions the template asks ---

        // Is the field on the selected case type's layout? Answers whether a value may be
        // sent, which is not the same as whether the advisor is asked for it.
        private onLayout = (name: string): boolean => {
            return this.fields[name] != undefined;
        };

        // Does the form show this field? Listed for the chosen case type, and known to
        // Salesforce - either on the layout or resolved from the object describe.
        has = (name: string): boolean => {
            return this.shown[name] === true && this.fields[name] != undefined;
        };

        label = (name: string, fallback: string = ""): string => {
            const field = this.fields[name];
            return (field && field.label) ? field.label : fallback;
        };

        // Mandatory on the layout advisors use in Salesforce. The integration user sits on a
        // fuller layout that marks almost nothing required, so those flags never reach us and
        // an advisor could otherwise save a case Salesforce would have refused.
        //
        // TEMPORARY, like formFields - once aosupport is on the advisor layout the real flags
        // arrive with the describe and this list can go.
        private static requiredFields: string[] = [
            "Account_Name__c",
            "Account_Type__c",
            "Beneficiaries_Y_N__c",
            "Investment_Type__c",
            "Investment_Names_Amounts__c",
            "Funding_Amount__c",
            "Advisory_Fee__c",
            "Signing_Method__c",
            "Signing_Priority__c",
            "Signer_Contact_Information__c",
            "Product_Name__c",
            "Illustration_Needed__c",
            "Product_Type__c",
            "Purpose__c",
            "Benefit_Amount__c",
            "Commission_Type__c",
            "Driver_s_License_On_File__c"
        ];

        isRequired = (name: string): boolean => {
            const field = this.fields[name];

            if (field && field.required) return true;

            return PopSalesforceCaseNewController.requiredFields.indexOf(name) >= 0;
        };

        // Salesforce's own field help, shown as the info tooltip beside the label.
        helpText = (name: string): string => {
            const field = this.fields[name];
            return (field && field.helpText) ? field.helpText : "";
        };

        // Salesforce only asks who the beneficiaries are once the advisor says there are some.
        wantsBeneficiaries = (): boolean => {
            return this.model["Beneficiaries_Y_N__c"] === "Yes";
        };

        // Answering "No" after typing names clears them, so nothing stale is posted.
        beneficiariesChanged = (): void => {
            if (this.wantsBeneficiaries()) return;

            this.model["Beneficiaries__c"] = null;
            this.model["Contingent_Beneficiary_ies__c"] = null;
        };

        // Returns the same array instance every call - handing back a fresh [] would make
        // ng-options/ng-repeat see a new reference on each digest and loop.
        private static noOptions: domains.ISalesforceCasePicklistOption[] = [];

        options = (name: string): domains.ISalesforceCasePicklistOption[] => {
            const field = this.fields[name];
            return (field && field.picklistValues)
                ? field.picklistValues
                : PopSalesforceCaseNewController.noOptions;
        };

        isInvalid = (name: string): boolean => {
            return this.invalidFields.indexOf(name) >= 0;
        };

        // Does any field in a section apply to the selected case type? Keeps empty section
        // headers off the form.
        hasAny = (...names: string[]): boolean => {
            return names.some(name => this.has(name));
        };

        // --- Multipicklists ---
        // Salesforce takes these semicolon-delimited; the form keeps them as an array and the
        // API joins them.
        //
        // Each box binds straight to `picked` on its own option object. ng-model has to be an
        // expression Angular can assign back into, and it must stay a plain property path:
        // an earlier version bound to vm.pickState('Field')[o.value], and assigning through
        // that function call never reached the model, so every box sprang back unticked on
        // the next digest. The options are reloaded with the case type, so the flags reset on
        // their own.
        syncPicks = (name: string): void => {
            const picked = this.options(name)
                .filter(option => option.picked)
                .map(option => option.value);

            this.model[name] = picked.length > 0 ? picked : null;
        };

        // --- Lookups: every match is a live Salesforce record ---

        searchContacts = (term: string) => this.search("contact", term);
        searchUsers = (term: string) => this.search("user", term);
        searchHouseholds = (term: string) => this.search("household", term);

        private search(lookup: string, term: string): ng.IPromise<domains.ISalesforceLookupResult[]> {
            return this.api.searchLookup(this.scope(), lookup, term)
                .then(resp => resp.data, () => []);
        }

        onSelectContact = (item: domains.ISalesforceLookupResult): void => {
            this.model["ContactId"] = item.id;

            // Salesforce ties the household to the contact, so fill it in to match rather
            // than making the advisor look it up again. Still editable afterwards.
            if (item.householdId && this.onLayout("FinServ__Household__c")) {
                this.model["FinServ__Household__c"] = item.householdId;
                this.selectedHousehold = { id: item.householdId, name: item.householdName };
            }
        };

        onSelectSecondaryContact = (item: domains.ISalesforceLookupResult): void => {
            this.model["Client_Name_Secondary__c"] = item.id;
        };

        onSelectHousehold = (item: domains.ISalesforceLookupResult): void => {
            this.model["FinServ__Household__c"] = item.id;
        };

        onSelectAdvisorUser = (item: domains.ISalesforceLookupResult): void => {
            this.model["Advisor_User__c"] = item.id;
        };

        onSelectAnalyst = (item: domains.ISalesforceLookupResult): void => {
            this.model["Anaylst__c"] = item.id;
        };

        onSelectOwner = (item: domains.ISalesforceLookupResult): void => {
            this.model["OwnerId"] = item.id;
        };

        // A cleared or half-typed lookup has to clear the id behind it, otherwise the form
        // would keep posting a record the advisor no longer sees.
        //
        // This must never assign to the input's own ng-model. Doing so mid-change makes
        // Angular re-render the input from the value just written, wiping whatever the
        // advisor typed - which reads as "the field won't accept input". Because
        // typeahead-editable is false, ng-model is only ever a record object once something
        // real is picked, so it is already the signal we need.
        onLookupChanged = (name: string, selection: any): void => {
            if (!selection || !selection.id) this.model[name] = null;
        };

        // --- Case type ---

        next = (): void => {
            if (!this.selectedRecordType) return;

            this.step = 2;

            this.loadForm(this.selectedRecordType.id);
        };

        // Back to the case-type list. Whatever was typed stays put, so returning to the same
        // type doesn't lose the advisor's work; switching type drops what no longer applies.
        back = (): void => {
            this.step = 1;
        };

        private loadForm(recordTypeId: string): void {
            this.loading = true;
            this.invalidFields = [];
            this.errorMessage = null;

            this.api.getNewForm(this.scope(), recordTypeId)
                .then(resp => {
                    this.fields = {};

                    (resp.data.form.fields || []).forEach(field => this.fields[field.name] = field);

                    const developerName = this.selectedRecordType ? this.selectedRecordType.developerName : "";

                    this.shown = {};

                    this.formFieldsFor(developerName).forEach(name => this.shown[name] = true);

                    this.model.RecordTypeId = recordTypeId;

                    this.dropFieldsOutsideCaseType();
                    this.applySalesforceDefaults();

                    // Default the advisor to whoever is signed in; they can search to reassign.
                    // A servicer gets no default here - the advisor on a case they open is
                    // somebody else, so they are asked rather than guessed at.
                    const defaultAdvisor = resp.data.defaultAdvisorUser;

                    if (!this.model["Advisor_User__c"] && defaultAdvisor && this.onLayout("Advisor_User__c")) {
                        this.model["Advisor_User__c"] = defaultAdvisor.id;
                        this.selectedAdvisorUser = defaultAdvisor;
                    }

                    // Whoever opens the case owns it. Salesforce would otherwise leave it with
                    // the integration user or route it to a queue.
                    const defaultOwner = resp.data.defaultOwner;

                    if (!this.model["OwnerId"] && defaultOwner && this.onLayout("OwnerId")) {
                        this.model["OwnerId"] = defaultOwner.id;
                        this.selectedOwner = defaultOwner;
                    }

                    // A case opened from a household dashboard belongs to that household.
                    if (!this.model["FinServ__Household__c"] && this.household && this.onLayout("FinServ__Household__c")) {
                        this.model["FinServ__Household__c"] = this.household.id;
                        this.selectedHousehold = this.household;
                    }
                }, err => this.gs.showError(err))
                .finally(() => this.loading = false);
        }

        // Switching case type can drop fields. Anything the new type doesn't have must go,
        // or it would be posted for a case that has no such field.
        private dropFieldsOutsideCaseType(): void {
            Object.keys(this.model).forEach(name => {
                if (name === "RecordTypeId") return;

                if (this.has(name)) return;

                if (PopSalesforceCaseNewController.managedFields.indexOf(name) >= 0) return;

                delete this.model[name];
            });

            if (!this.onLayout("ContactId")) this.selectedContact = null;
            if (!this.onLayout("Client_Name_Secondary__c")) this.selectedSecondaryContact = null;
            if (!this.onLayout("FinServ__Household__c")) this.selectedHousehold = null;
            if (!this.onLayout("Advisor_User__c")) this.selectedAdvisorUser = null;
            if (!this.onLayout("Anaylst__c")) this.selectedAnalyst = null;
            if (!this.onLayout("OwnerId")) this.selectedOwner = null;
        }

        // Honour the picklist defaults Salesforce reports for this case type - a new
        // open-investment case starts at "New", same as it would in Salesforce.
        private applySalesforceDefaults(): void {
            angular.forEach(this.fields, field => {
                if (field.type !== "picklist" || this.model[field.name]) return;

                const preset = field.picklistValues.filter(option => option.isDefault)[0];

                if (preset) this.model[field.name] = preset.value;
            });
        }

        // --- Save ---

        // Check what Salesforce said is mandatory before posting, so the advisor gets the
        // message inline instead of as a rejection from the API.
        private missingRequiredFields(): string[] {
            const missing: string[] = [];

            angular.forEach(this.fields, field => {
                // A mandatory field the form doesn't show is one Salesforce defaults or fills
                // itself, so it is not the advisor's to answer for.
                if (!this.has(field.name) || !this.isRequired(field.name)) return;

                // Beneficiary detail is only asked for when the answer is Yes.
                if (!this.wantsBeneficiaries() &&
                    (field.name === "Beneficiaries__c" || field.name === "Contingent_Beneficiary_ies__c")) return;

                const value = this.model[field.name];

                const empty = value == null || value === "" ||
                    (angular.isArray(value) && value.length === 0);

                if (empty) missing.push(field.name);
            });

            return missing;
        }

        ok = (): void => {
            if (this.saving) return;

            this.invalidFields = this.missingRequiredFields();

            if (this.invalidFields.length > 0) {
                const names = this.invalidFields.map(name => this.label(name, name));

                this.errorMessage = `Salesforce needs these filled in first: ${names.join(", ")}.`;

                this.scrollToFirstProblem();

                return;
            }

            this.errorMessage = null;
            this.saving = true;

            this.api.createCase(this.scope(), this.model)
                .then(resp => {
                    const created = resp.data;

                    toastr.success(`Case ${created.caseNumber} created in Salesforce.`);

                    this.$scope.$parent.$uibModalInstance.close(created);
                }, err => {
                    // Salesforce refusing the case is an expected outcome - show its own
                    // wording and flag the fields it named.
                    const rejection = err && err.data;

                    if (rejection && rejection.error) {
                        this.errorMessage = rejection.error;
                        this.invalidFields = rejection.errorFields || [];
                        this.scrollToFirstProblem();
                    } else {
                        this.gs.showError(err);
                    }
                })
                .finally(() => this.saving = false);
        };

        // Scrolls the first field at fault into view. Without this the advisor presses Create
        // at the bottom of a long form and nothing appears to happen - the alert is at the top,
        // off screen. Runs after a digest so the red styling is on the page to scroll to.
        private scrollToFirstProblem(): void {
            this.$timeout(() => {
                const body = document.querySelector("#popSalesforceCaseNew .modal-body-scroll") as HTMLElement;

                if (!body) return;

                const target = body.querySelector(".field-rejected") as HTMLElement;

                if (!target) {
                    // Salesforce refused the case without naming a field - show the message.
                    body.scrollTop = 0;
                    return;
                }

                // Offsets are measured against the scroll container, not the page, so this
                // holds wherever the modal sits.
                const shift = target.getBoundingClientRect().top - body.getBoundingClientRect().top;

                body.scrollTop = Math.max(0, body.scrollTop + shift - 20);
            });
        }

        // Short summary for the footer, which never scrolls out of view.
        problemSummary = (): string => {
            if (!this.errorMessage) return "";

            if (this.invalidFields.length > 0) {
                return this.invalidFields.length === 1
                    ? "1 field needs attention"
                    : `${this.invalidFields.length} fields need attention`;
            }

            return "Salesforce refused the case";
        };

        cancel = (): void => {
            this.$scope.$parent.$uibModalInstance.dismiss();
        };

        // Advisor unless the caller said otherwise, so an existing call site that opens this
        // modal without a scope keeps working.
        private scope(): services.SalesforceCaseScope {
            return this.apiScope === "servicer" ? "servicer" : "advisor";
        }

        $onInit(): void {
            this.loading = true;

            this.api.getRecordTypes(this.scope())
                .then(resp => {
                    this.recordTypes = resp.data || [];

                    const preset = this.recordTypes.filter(rt => rt.isDefault)[0];

                    this.selectedRecordType = preset || this.recordTypes[0];

                    this.loading = false;
                }, err => {
                    this.gs.showError(err);
                    this.loading = false;
                });
        }
    }

    class PopSalesforceCaseNew implements ng.IComponentOptions {
        public bindings: any;
        public controller: any;
        public controllerAs: string;
        public templateUrl: string;

        constructor() {
            this.bindings = {
                // Both arrive through the modal's resolve.
                apiScope: "<",
                household: "<"
            };
            this.controller = PopSalesforceCaseNewController;
            this.controllerAs = "vm";
            this.templateUrl = "spa/advisor-module/components/pop-salesforce-case-new.html";
        }
    }
    angular.module(Constants.APP).component("popSalesforceCaseNew", new PopSalesforceCaseNew());
}
